import { TimeType } from '@repo/shared/dtos/generic'
import { getLocale, useControlledOpen, useT } from '@repo/ui/hooks'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { cn, formatDate } from '@repo/ui/utils'
import { format, startOfDay } from 'date-fns'
import { ChevronDown, ChevronUp } from 'lucide-react'
import {
  ButtonHTMLAttributes,
  createContext,
  forwardRef,
  HTMLAttributes,
  memo,
  useContext,
} from 'react'

const variantClasses: Record<string, string> = {
  default:
    'border-primary-100 text-primary bg-white hover:border-primary-200 transition-colors',
  secondary:
    'border-primary-500 text-primary-200 bg-primary-900 hover:border-primary-200 transition-colors hover:text-primary-100',
  transparent:
    'border-primary-500 text-primary-200 bg-transparent hover:border-primary-200 transition-colors hover:text-primary-100',
}

type CardVariant = keyof typeof variantClasses

const CollapsibleCardContext = createContext<{
  open: boolean
  setOpen: (open: boolean) => void
  variant: CardVariant
} | null>(null)

interface CollapsibleCardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

const CollapsibleCard = forwardRef<HTMLDivElement, CollapsibleCardProps>(
  (
    {
      className,
      variant = 'default',
      open: openProp,
      defaultOpen,
      onOpenChange,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = useControlledOpen({
      openProp,
      defaultOpen,
      onOpenChange,
    })
    return (
      <CollapsibleCardContext.Provider value={{ open, setOpen, variant }}>
        <div
          ref={ref}
          className={cn(
            'rounded-lg border',
            variantClasses[variant],
            className,
          )}
          {...props}
        />
      </CollapsibleCardContext.Provider>
    )
  },
)
CollapsibleCard.displayName = 'CollapsibleCard'

const dividerClasses: Record<CardVariant, string> = {
  default: 'divide-primary-100',
  secondary: 'divide-primary-500',
  transparent: 'divide-primary-500',
}

const CollapsibleCardHeader = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const ctx = useContext(CollapsibleCardContext)
  if (!ctx)
    throw new Error(
      'CollapsibleCardHeader must be used within a CollapsibleCard',
    )
  return (
    <div
      ref={ref}
      className={cn(
        dividerClasses[ctx.variant],
        'flex h-fit items-center divide-x',
        className,
      )}
      {...props}
    />
  )
})
CollapsibleCardHeader.displayName = 'CollapsibleCardHeader'

const CollapsibleCardLeftButtons = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const ctx = useContext(CollapsibleCardContext)
  if (!ctx)
    throw new Error(
      'CollapsibleCardLeftButtons must be used within a CollapsibleCard',
    )
  return (
    <div
      ref={ref}
      data-state={ctx.open ? 'open' : 'closed'}
      className={cn('flex h-fit flex-shrink-0 items-center', className)}
      {...props}
    />
  )
})
CollapsibleCardLeftButtons.displayName = 'CollapsibleCardLeftButtons'

const CollapsibleCardTitle = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, ...props }, ref) => {
  const ctx = useContext(CollapsibleCardContext)
  if (!ctx)
    throw new Error(
      'CollapsibleCardTitle must be used within a CollapsibleCard',
    )
  return (
    <button
      ref={ref}
      type="button"
      onClick={() => ctx.setOpen(!ctx.open)}
      data-state={ctx.open ? 'open' : 'closed'}
      className={cn(
        'flex flex-1 items-center justify-between gap-2 px-3 py-2',
        className,
      )}
      {...props}
    >
      <div
        data-state={ctx.open ? 'open' : 'closed'}
        className={cn(
          'text-left',
          ctx.open ? 'line-clamp-none' : 'line-clamp-1',
        )}
      >
        {children}
      </div>

      <div className="max-w-fit flex-shrink-0">
        {ctx.open ? (
          <ChevronUp className="size-5" />
        ) : (
          <ChevronDown className="size-5" />
        )}
      </div>
    </button>
  )
})
CollapsibleCardTitle.displayName = 'CollapsibleCardTitle'

const borderClasses: Record<CardVariant, string> = {
  default: 'border-primary-100',
  secondary: 'border-primary-500',
  transparent: 'border-primary-500',
}

// Visible if open
const CollapsibleCardContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const ctx = useContext(CollapsibleCardContext)
  if (!ctx)
    throw new Error(
      'CollapsibleCardContent must be used within a CollapsibleCard',
    )
  if (!ctx.open) return null
  return (
    <div
      ref={ref}
      data-state={ctx.open ? 'open' : 'closed'}
      className={cn(
        borderClasses[ctx.variant],
        'flex flex-col gap-2 border-t p-4',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
})
CollapsibleCardContent.displayName = 'CollapsibleCardContent'

const CollapsibleCardFooter = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const ctx = useContext(CollapsibleCardContext)
  if (!ctx)
    throw new Error(
      'CollapsibleCardFooter must be used within a CollapsibleCard',
    )
  return (
    <div
      ref={ref}
      data-state={ctx.open ? 'open' : 'closed'}
      className={cn(
        borderClasses[ctx.variant],
        'mt-2 flex items-center gap-2',
        className,
      )}
      {...props}
    />
  )
})
CollapsibleCardFooter.displayName = 'CollapsibleCardFooter'

export {
  CollapsibleCard,
  CollapsibleCardContent,
  CollapsibleCardFooter,
  CollapsibleCardHeader,
  CollapsibleCardLeftButtons,
  CollapsibleCardTitle,
}

export const ItemCreatedDate = memo(function ({
  className,
  createdAt,
  labelClassName,
}: {
  className?: string
  createdAt: TimeType
  labelClassName?: string
}) {
  if (isNaN(createdAt)) return null

  const { tc } = useT()
  const createdDateObj = new Date(createdAt)
  const createdDate = startOfDay(createdDateObj)
  return (
    <TooltipWrapper
      content={tc('createdAt', {
        cfl: true,
        date: format(createdDateObj, 'EEEE, dd MMMM yyyy, HH:mm', {
          locale: getLocale(),
        }),
      })}
    >
      <div
        className={cn(
          'text-primary-500 flex w-fit items-center gap-1 text-sm',
          className,
        )}
      >
        <span className={labelClassName}>
          {tc('created', { cfl: true, colon: true })}
        </span>
        {formatDate(createdDate)}
      </div>
    </TooltipWrapper>
  )
})

export const ItemUpdatedDate = memo(function ({
  className,
  labelClassName,
  updatedAt,
}: {
  className?: string
  labelClassName?: string
  updatedAt: TimeType
}) {
  if (isNaN(updatedAt)) return null
  const { tc } = useT()
  return (
    <TooltipWrapper
      content={tc('lastUpdated', {
        cfl: true,
        date: format(new Date(updatedAt), 'EEEE, dd MMMM yyyy, HH:mm', {
          locale: getLocale(),
        }),
      })}
    >
      <div
        className={cn(
          'text-primary-500 flex w-fit items-center gap-1 text-sm',
          className,
        )}
      >
        <span className={labelClassName}>
          {tc('updated', { cfl: true, colon: true })}
        </span>
        {formatDate(updatedAt)}
      </div>
    </TooltipWrapper>
  )
})
